import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getSessionToken, clearSessionToken } from '../utils/session';
import { navigateToLogin } from '../utils/navigation';

const EMPLOYEE_LOGIN_KEY = 'employeeLogin';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${import.meta.env.VITE_WAREHOUSE_BACKEND_BASE_URL}/`,
  prepareHeaders: async (headers) => {
    const token = await getSessionToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// A 401 for the token that's still stored means the session is missing,
// invalid, or expired - nothing else will work until the user logs in
// again, so clear the session and send them back to the login screen
// with an explanation. A 401 whose token is no longer the stored one is
// stale: the request was dispatched before the session changed (e.g. an
// in-flight query while the user re-logged in). Wiping the session for
// those would erase the brand-new token, so they're retried once with
// the current token instead. The session endpoint's own 401s are failed
// logins, not expired sessions, so they're left for the login screen to
// display itself.
const baseQuery = async (args, apiContext, extraOptions) => {
  const url = typeof args === 'string' ? args : args.url;
  const execute = async () => {
    const tokenAtDispatch = await getSessionToken();
    const result = await rawBaseQuery(args, apiContext, extraOptions);
    return { tokenAtDispatch, result };
  };

  let { tokenAtDispatch, result } = await execute();

  if (result.error?.status === 401 && url !== 'session') {
    if ((await getSessionToken()) !== tokenAtDispatch) {
      // The session changed while this request was in flight, so its
      // 401 was for stale credentials - retry once with the new token.
      ({ tokenAtDispatch, result } = await execute());
    }
    // If the token changed again during the retry, this 401 is stale
    // too - leave the newest session alone and return the result as-is.
    if (
      result.error?.status === 401 &&
      (await getSessionToken()) === tokenAtDispatch
    ) {
      await clearSessionToken();
      localStorage.removeItem(EMPLOYEE_LOGIN_KEY);
      navigateToLogin();
    }
  }

  return result;
};

// Base API slice for RTK Query. Feature endpoints inject into this via
// api.injectEndpoints(). The base URL comes from
// VITE_WAREHOUSE_BACKEND_BASE_URL in the env files.
export const api = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: ['Batches', 'Batch'],
  endpoints: () => ({}),
});
